import React from 'react';
import NextHead from 'next/head';
import { useRouter } from 'next/router';
import ProptTypes from 'prop-types';
import queryString from 'utils/queryString';

import SETTINGS from '../../settings';

const HEAD_KEY = {
  CHAR_SET: 'charSet',
  HTTP_EQUIV_COMPATIBLE: 'httpEquivCompatible',
  HTTP_EQUIV_ACCEPT: 'httpEquivAccept',
  VIEWPORT: 'viewport',
  GTM: 'gtm',
  TITLE: 'title',
  DESCRIPTION: 'description',
  KEYWORDS: 'keywords',
  ICON: 'icon',
  TWITTER_IMAGE: 'twitterImage',
  TWITTER_CARD: 'twitterCard',
  TWITTER_SITE: 'twitterSite',
  TWITTER_TITLE: 'twitterTitle',
  TWITTER_DESCRIPTION: 'twitterDescription',
  TWITTER_CREATOR: 'twitterCreator',
  OG_SITE: 'ogSite',
  OG_TITLE: 'ogTitle',
  OG_TYPE: 'ogType',
  OG_URL: 'ogUrl',
  OG_IMAGE: 'ogImage',
  OG_DESCRIPTION: 'ogDescription',
  CANONICAL: 'canonical',
  FB_VERIFICATION: 'fbVerification',
};

const Head = props => {
  const router = useRouter();

  const pageUrl = `${SETTINGS.HOST}${queryString.parseUrl(router.asPath).url}`;

  return (
    <NextHead>
      <meta key={HEAD_KEY.CHAR_SET} charSet="utf-8" />
      <meta key={HEAD_KEY.HTTP_EQUIV_COMPATIBLE} httpEquiv="X-UA-Compatible" content="IE=edge" />
      <meta
        key={HEAD_KEY.HTTP_EQUIV_ACCEPT}
        httpEquiv="Accept-CH"
        content="DPR, Viewport-Width, Width"
      />
      <meta
        key={HEAD_KEY.VIEWPORT}
        name="viewport"
        content="width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=no"
      />

      <title key={HEAD_KEY.TITLE}>{`${props.siteName}: ${props.title}`}</title>
      <meta key={HEAD_KEY.DESCRIPTION} name="description" content={props.description} />
      <meta key={HEAD_KEY.KEYWORDS} name="keywords" content={props.keywords} />
      <link key={HEAD_KEY.ICON} rel="icon" href="/static/favicon.png" />
      <link key={HEAD_KEY.CANONICAL} rel="canonical" href={pageUrl} />

      <script
        key={HEAD_KEY.GTM}
        type="text/javascript"
        dangerouslySetInnerHTML={{
          __html: `
          (function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
          new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
          j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
          'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
          })(window,document,'script','dataLayer','${SETTINGS.SCRIPTS.GTM_ID}');`,
        }}
      />

      <meta key={HEAD_KEY.TWITTER_IMAGE} name="twitter:image" content={props.image} />
      <meta key={HEAD_KEY.TWITTER_CARD} name="twitter:card" content="summary_large_image" />
      <meta key={HEAD_KEY.TWITTER_SITE} name="twitter:site" content="@parabopress" />
      <meta
        key={HEAD_KEY.TWITTER_TITLE}
        name="twitter:title"
        content={`${props.siteName}: ${props.title}`}
      />
      <meta
        key={HEAD_KEY.TWITTER_DESCRIPTION}
        name="twitter:description"
        content={props.description}
      />

      <meta key={HEAD_KEY.OG_SITE} property="og:site_name" content={props.siteName} />
      <meta
        key={HEAD_KEY.OG_TITLE}
        property="og:title"
        content={`${props.siteName}: ${props.title}`}
      />
      <meta key={HEAD_KEY.OG_TYPE} property="og:type" content={props.ogType || "website"} />
      <meta key={HEAD_KEY.OG_URL} property="og:url" content={pageUrl} />
      <meta key={HEAD_KEY.OG_IMAGE} property="og:image" content={props.image} />
      <meta key={HEAD_KEY.OG_DESCRIPTION} property="og:description" content={props.description} />

      <meta
        key={HEAD_KEY.FB_VERIFICATION}
        name="facebook-domain-verification"
        content="96v9vbu83mhdhhbjofxeaa8rvyupng"
      />
    </NextHead>
  );
};

Head.propTypes = {
  siteName: ProptTypes.string,
  title: ProptTypes.string,
  description: ProptTypes.string,
  keywords: ProptTypes.string,
  image: ProptTypes.string,
  ogType: ProptTypes.string,
};

Head.defaultProps = {
  siteName: 'Parabo Press',
  title: 'Order Photo Prints, Books, Cards & Display',
  description:
    'Our online photo printing service brings digital memories to life with thoughtfully-designed prints, books, wall art and décor. Print life’s moments, big and small on matte Square Prints, giant Engineer Prints and more.',
  keywords: 'Parabo Press, Photo print, Photo book, Fine Art Prints, Print Instagram Photos',
  image:
    'https://photolane-templates.s3.amazonaws.com/square-print-small/4.0.0/product_images/web/homepage.jpg',
};

export default Head;
